import React, { useState } from 'react';
import { Link, router, usePage } from '@inertiajs/react';
import Layout from '../components/Layout.jsx';

const COURSE_PORTAL_URL =
  'https://123ceu.com/signin/openid?provider=177522925469917&redirectUrl=https%3A%2F%2F123ceu.com%2F&action=login';

function WelcomeHeader({ firstName, subtitle }) {
  return (
    <div>
      <h1 className="text-3xl font-bold tracking-tight text-gray-900">
        {firstName ? `Welcome back, ${firstName}` : 'Welcome back'}
      </h1>
      {subtitle && <p className="mt-1 text-sm text-gray-500">{subtitle}</p>}
    </div>
  );
}

function StudentTrainingHero({ hasActiveSubscription, emailVerified }) {
  const resume = hasActiveSubscription;
  const title = resume ? 'Resume Your Training' : 'Start Your Training Today';
  const description = resume
    ? 'Pick up where you left off — courses, videos, quizzes, and certificates are all in one place.'
    : "Set up your training and start working toward your certification. It only takes a minute.";
  const ctaLabel = resume ? 'Resume Training' : 'Get Started';
  const ctaHref = resume ? COURSE_PORTAL_URL : '/student_purchase/new';
  const isExternal = resume;

  const handleResumeClick = (e) => {
    if (!emailVerified) {
      e.preventDefault();
      window.location.href = '/email_verifications/pending';
    }
  };

  const ctaClass = "shrink-0 inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-lg text-indigo-700 bg-white hover:bg-indigo-50 shadow-sm transition";

  return (
    <div className="relative overflow-hidden bg-gradient-to-br from-indigo-600 via-indigo-700 to-purple-700 rounded-2xl p-8 shadow-lg text-white">
      <div className="absolute -right-16 -top-16 w-72 h-72 bg-white/10 rounded-full blur-3xl" aria-hidden="true" />
      <div className="absolute -left-12 -bottom-12 w-56 h-56 bg-white/5 rounded-full blur-2xl" aria-hidden="true" />

      <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
        <div className="min-w-0 max-w-xl">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 mb-3 rounded-full bg-white/15 text-xs font-medium backdrop-blur-sm">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            {resume ? 'Continue Learning' : 'Get Certified'}
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold">{title}</h2>
          <p className="mt-2 text-sm sm:text-base text-indigo-100">{description}</p>
          {resume && !emailVerified && (
            <p className="mt-2 text-xs text-indigo-100">Verify your email first to access training.</p>
          )}
        </div>
        {isExternal ? (
          <a href={ctaHref} onClick={handleResumeClick} className={ctaClass}>
            {ctaLabel}
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
          </a>
        ) : (
          <Link href={ctaHref} className={ctaClass}>
            {ctaLabel}
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
          </Link>
        )}
      </div>
    </div>
  );
}

function GroupManagerHero({ overview }) {
  if (!overview) return null;

  return (
    <div className="relative overflow-hidden bg-gradient-to-br from-indigo-500 via-indigo-600 to-purple-600 rounded-2xl p-8 shadow-lg text-white">
      <div className="absolute -right-20 -top-20 w-80 h-80 bg-white/20 rounded-full blur-3xl" aria-hidden="true" />
      <div className="absolute right-1/3 -bottom-24 w-64 h-64 bg-purple-300/25 rounded-full blur-3xl" aria-hidden="true" />

      <div className="relative flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
        <div className="min-w-0">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 mb-3 rounded-full bg-white/10 text-xs font-medium backdrop-blur-sm">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            {overview.facility_type_display}
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold">{overview.facility_name}</h1>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 shrink-0">
          <Link
            href={`/facilities/${overview.facility_id}/invite_user`}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-lg text-white bg-white/10 hover:bg-white/20 backdrop-blur-sm transition border border-white/20"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" /></svg>
            Add Team Member
          </Link>
          <Link
            href={`/facilities/${overview.facility_id}/billing/purchase`}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-lg text-indigo-700 bg-white hover:bg-indigo-50 shadow-sm transition"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" /></svg>
            Purchase Training
          </Link>
        </div>
      </div>
    </div>
  );
}

function ManageAFacilityCallToAction() {
  return (
    <section
      aria-labelledby="manage-a-facility-heading"
      className="bg-white border border-indigo-200 rounded-2xl p-6 shadow-sm"
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-10 h-10 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center shrink-0" aria-hidden="true">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          <div className="min-w-0">
            <h2 id="manage-a-facility-heading" className="text-lg font-semibold text-gray-900">
              Managing training for a team?
            </h2>
            <p className="mt-1 text-sm text-gray-600">
              Add a facility to your account so you can invite staff, buy training seats, and track
              everyone&apos;s progress in one place. You keep this account — your training history,
              certificates, and sign-in details all stay exactly as they are.
            </p>
          </div>
        </div>
        <Link
          href="/facilities/convert"
          className="shrink-0 inline-flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 shadow-sm transition focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
        >
          Set Up a Facility
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
          </svg>
        </Link>
      </div>
    </section>
  );
}

const STAT_ACCENTS = {
  indigo:  { bg: 'bg-indigo-50',  text: 'text-indigo-600',  hoverBorder: 'hover:border-indigo-400' },
  emerald: { bg: 'bg-emerald-50', text: 'text-emerald-600', hoverBorder: 'hover:border-emerald-400' },
  amber:   { bg: 'bg-amber-50',   text: 'text-amber-600',   hoverBorder: 'hover:border-amber-400' },
};

function StatCard({ label, value, sublabel, href, icon, accent = 'indigo', progress }) {
  const a = STAT_ACCENTS[accent];
  const body = (
    <div className={`group relative h-full bg-white border border-gray-200 rounded-2xl p-6 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition ${a.hoverBorder}`}>
      <div className="flex items-start justify-between">
        <div className={`w-10 h-10 ${a.bg} ${a.text} rounded-xl flex items-center justify-center`}>
          {icon}
        </div>
        {href && (
          <svg className="w-4 h-4 text-gray-300 group-hover:text-gray-500 transition" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
          </svg>
        )}
      </div>
      <p className="mt-4 text-sm font-medium text-gray-500">{label}</p>
      <p className="mt-1 text-3xl font-bold tracking-tight text-gray-900">{value}</p>
      {progress != null && (
        <div className="mt-3 h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
          <div className={`h-full ${a.text.replace('text-', 'bg-')} transition-all duration-500`} style={{ width: `${Math.max(0, Math.min(100, progress))}%` }} />
        </div>
      )}
      {sublabel && <p className="mt-2 text-xs text-gray-500">{sublabel}</p>}
    </div>
  );
  return href ? <Link href={href} className="block h-full">{body}</Link> : body;
}

function GroupManagerOverview({ overview }) {
  if (!overview) return null;
  const assignedPct = overview.seats_purchased > 0
    ? Math.round((overview.seats_assigned / overview.seats_purchased) * 100)
    : 0;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      <StatCard
        label="Team Members"
        value={overview.team_size}
        sublabel={overview.pending_invites > 0 ? `${overview.pending_invites} pending invite${overview.pending_invites === 1 ? '' : 's'}` : 'All invitations accepted'}
        href="/team"
        accent="indigo"
        icon={
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        }
      />
      <StatCard
        label="Available Training Seats"
        value={overview.seats_available}
        sublabel={`${overview.seats_assigned} of ${overview.seats_purchased} assigned`}
        href="/assign-training"
        accent="emerald"
        progress={assignedPct}
        icon={
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
          </svg>
        }
      />
      <StatCard
        label="Training Purchased"
        value={overview.seats_purchased}
        sublabel="Across all training types"
        href={`/facilities/${overview.facility_id}/billing`}
        accent="amber"
        icon={
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 14l9-5-9-5-9 5 9 5z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
          </svg>
        }
      />
    </div>
  );
}

function PendingInvitations({ invitations, onAccept, onDecline, actionLoading }) {
  if (invitations.length === 0) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 bg-amber-50 text-amber-600 rounded-lg flex items-center justify-center">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" /></svg>
        </div>
        <h2 className="text-lg font-semibold text-gray-900">Pending Invitations</h2>
      </div>
      <div className="space-y-3">
        {invitations.map((invitation) => (
          <div key={invitation.token} className="border border-amber-100 rounded-xl p-4 bg-amber-50/50">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="min-w-0">
                <h3 className="text-base font-semibold text-gray-900">Invited to {invitation.facility_name}</h3>
                <p className="text-sm text-gray-600">{invitation.facility_type_display}</p>
              </div>
              <div className="flex gap-2 shrink-0">
                <button
                  onClick={() => onAccept(invitation.token)}
                  disabled={actionLoading === invitation.token}
                  className="inline-flex items-center px-4 py-2 text-sm font-semibold rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 transition shadow-sm"
                >
                  Accept
                </button>
                <button
                  onClick={() => onDecline(invitation.token)}
                  disabled={actionLoading === invitation.token}
                  className="inline-flex items-center px-4 py-2 text-sm font-semibold rounded-lg text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 disabled:opacity-50 transition"
                >
                  Decline
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Announcements({ announcements }) {
  if (announcements.length === 0) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 bg-indigo-50 text-indigo-600 rounded-lg flex items-center justify-center">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" /></svg>
        </div>
        <h2 className="text-lg font-semibold text-gray-900">Announcements</h2>
      </div>
      <div className="space-y-3">
        {announcements.map((announcement) => (
          <div key={announcement.id} className="border border-gray-200 rounded-xl p-4 hover:border-gray-300 transition">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="min-w-0">
                <h3 className="text-base font-semibold text-gray-900">{announcement.title}</h3>
                <p className="mt-1 text-sm text-gray-600 whitespace-pre-line">{announcement.description}</p>
              </div>
              {announcement.cta_url && (
                <a
                  href={announcement.cta_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="shrink-0 inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 shadow-sm transition"
                >
                  {announcement.cta_label}
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                </a>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ActiveTrainingCard({ licenses }) {
  if (licenses.length === 0) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 bg-emerald-50 text-emerald-600 rounded-lg flex items-center justify-center">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
        </div>
        <h2 className="text-lg font-semibold text-gray-900">Your Active Training</h2>
      </div>
      <div className="space-y-3">
        {licenses.map((license) => {
          const urgent = license.days_until_expiration != null && license.days_until_expiration <= 30 && license.days_until_expiration > 0;
          return (
            <div key={license.id} className="border border-gray-200 rounded-xl p-4 hover:border-gray-300 transition">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-base font-semibold text-gray-900">{license.name}</h3>
                    <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">Active</span>
                  </div>
                  <p className="mt-1 text-sm text-gray-500">
                    {license.student_type} · {license.facility_type}
                  </p>
                </div>
                {license.expires_at && (
                  <div className={`text-right ${urgent ? 'text-amber-700' : 'text-gray-500'}`}>
                    <p className="text-xs uppercase tracking-wide font-medium">Expires</p>
                    <p className="text-sm font-semibold">{license.expires_at}</p>
                    {urgent && <p className="text-xs">in {license.days_until_expiration} days</p>}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Dashboard({
  announcements = [],
  pending_invitations = [],
  is_student,
  can_convert_to_group_account = false,
  has_active_subscription,
  active_licenses = [],
  group_manager_overview,
}) {
  const { auth } = usePage().props;
  const emailVerified = auth?.user?.email_verified;
  const firstName = auth?.user?.first_name;
  const isGroupManager = auth?.user?.user_type === 'group_manager';
  const [actionLoading, setActionLoading] = useState(null);

  const handleAcceptInvitation = (token) => {
    setActionLoading(token);
    router.post(`/invitations/${token}/accept`, {}, { onFinish: () => setActionLoading(null) });
  };

  const handleDeclineInvitation = (token) => {
    setActionLoading(token);
    router.post(`/invitations/${token}/decline`, {}, { onFinish: () => setActionLoading(null) });
  };

  const greetingSubtitle = is_student
    ? "Here's where you left off in your training."
    : isGroupManager
      ? "An overview of your facility, team, and training."
      : null;

  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-6 sm:p-8 space-y-6">
        <WelcomeHeader firstName={firstName} subtitle={greetingSubtitle} />

        <PendingInvitations
          invitations={pending_invitations}
          onAccept={handleAcceptInvitation}
          onDecline={handleDeclineInvitation}
          actionLoading={actionLoading}
        />

        {is_student && (
          <>
            <StudentTrainingHero hasActiveSubscription={has_active_subscription} emailVerified={emailVerified} />
            {has_active_subscription && active_licenses.length > 0 && (
              <ActiveTrainingCard licenses={active_licenses} />
            )}
          </>
        )}

        {is_student && can_convert_to_group_account && <ManageAFacilityCallToAction />}

        <Announcements announcements={announcements} />

        {isGroupManager && active_licenses.length > 0 && (
          <ActiveTrainingCard licenses={active_licenses} />
        )}

        {isGroupManager && (
          group_manager_overview ? (
            <>
              <GroupManagerHero overview={group_manager_overview} />
              <GroupManagerOverview overview={group_manager_overview} />
            </>
          ) : (
            <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-xl font-semibold text-gray-900">Get Started</h2>
                <Link href="/facilities/new" className="inline-flex items-center px-4 py-2 text-sm font-semibold rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 transition shadow-sm">
                  Add Facility
                </Link>
              </div>
              <p className="text-gray-600">Set up your facility to start managing team members and training.</p>
            </div>
          )
        )}
      </div>
    </Layout>
  );
}
